import { Injectable } from '@angular/core';
import { PagedList } from '@rma/generic/domain/to-be-sorted';
import { rmaShareReplay } from '@rma/generic/util/operators/rma-share-replay';
import {
  NotificationBellApiService,
  UserNotificationEventType,
  UserNotificationModel,
} from '@rma/notification-bell/private/api-user-notifications';
import { NotificationBellEventService } from '@rma/notification-bell/private/util-notification-content';
import { interval, map, merge, Observable, startWith, Subject, switchMap, take } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class NotificationBellFacadeService {
  public unreadNotificationsCount$: Observable<number>;
  public notifications$: Observable<PagedList<UserNotificationModel>>;

  private forceRefresh$ = new Subject<void>();

  public constructor(
    private readonly notificationBellApiService: NotificationBellApiService,
    private readonly notificationBellEventService: NotificationBellEventService,
  ) {
    const refreshTimer$ = interval(60 * 1000).pipe(startWith(0));
    const unreadNotificationsRefresh$ = merge(this.forceRefresh$, refreshTimer$).pipe(rmaShareReplay());

    const unreadNotificationsCount$ = unreadNotificationsRefresh$.pipe(
      switchMap(() => this.notificationBellApiService.getNotifications({ take: 0, onlyNew: true })),
      rmaShareReplay(),
    );

    this.unreadNotificationsCount$ = unreadNotificationsCount$.pipe(map((x) => x.total));
    this.notifications$ = this.notificationBellApiService.getNotifications();
  }

  public markAllAsRead(): void {
    this.notificationBellApiService
      .markAllAsRead()
      .pipe(take(1))
      .subscribe(() => {
        this.forceRefresh$.next();
      });
  }

  public markAsViewed(): void {
    this.notifications$
      .pipe(
        switchMap((notifications) => this.notificationBellEventService.markAllAs(notifications.results, UserNotificationEventType.View)),
        take(1),
      )
      .subscribe(() => this.forceRefresh$.next());
  }
}
